<template>
  <div v-show="isVisible" class="notifications-shell" :aria-hidden="!isVisible">
    <div class="notifications-shell__backdrop" @click="emit('close-notificaciones')" />
    <aside
      class="notifications-panel"
      role="dialog"
      :aria-label="$t('notificaciones.titulo')"
      @click.stop
    >
      <div class="title-bar-container sin-borde">
        <div class="title-bar-left">
          <h5 class="notifications-panel__title">
            <i class="fa-regular fa-bell notifications-panel__title-icon" aria-hidden="true" />
            {{ $t("notificaciones.titulo") }}
          </h5>
        </div>
        <button
          type="button"
          class="btn"
          :aria-label="$t('shared.ui.buttons.close')"
          @click="emit('close-notificaciones')"
        >
          <i class="fa-regular fa-times" aria-hidden="true" />
        </button>
      </div>

      <div class="notifications-panel__tabs" role="tablist">
        <button
          type="button"
          class="notifications-panel__tab"
          :class="{ 'notifications-panel__tab--active': pestañaActiva === 'sinLeer' }"
          role="tab"
          :aria-selected="pestañaActiva === 'sinLeer'"
          @click="pestañaActiva = 'sinLeer'"
        >
          <i class="fa-regular fa-envelope notifications-panel__tab-icon" aria-hidden="true" />
          {{ $t("notificaciones.tab_sin_leer") }}
        </button>
        <button
          type="button"
          class="notifications-panel__tab"
          :class="{ 'notifications-panel__tab--active': pestañaActiva === 'leidas' }"
          role="tab"
          :aria-selected="pestañaActiva === 'leidas'"
          @click="pestañaActiva = 'leidas'"
        >
          <i class="fa-regular fa-envelope-open notifications-panel__tab-icon" aria-hidden="true" />
          {{ $t("notificaciones.tab_leidas") }}
        </button>
      </div>

      <div class="notifications-panel__body">
        <NotificacionesList
          v-show="pestañaActiva === 'sinLeer'"
          :notificaciones="notificacionesSinLeer"
          :leida="false"
          @refrescar-notificaciones="cargarNotificaciones"
        />
        <NotificacionesList
          v-show="pestañaActiva === 'leidas'"
          :notificaciones="notificacionesLeidas"
          :leida="true"
        />
      </div>

      <div
        v-if="pestañaActiva === 'sinLeer' && notificacionesSinLeer.length > 0"
        class="notifications-panel__footer"
      >
        <button
          type="button"
          class="btn notifications-panel__mark-all-read"
          @click="marcarTodasLeidas"
        >
          <i class="fa-regular fa-envelope-open" aria-hidden="true" />
          {{ $t("notificaciones.marcar_todas_leidas") }}
        </button>
      </div>
    </aside>
  </div>
</template>

<script setup lang="ts">
import { inject, onMounted, onUnmounted, ref, watch, type Ref } from "vue";
import NotificacionesList from "./NotificacionesList.vue";
import { useApi } from "@api/integration";
import type { NotificacionModel } from "@api/api";

const props = defineProps({
  isVisible: Boolean,
});

const emit = defineEmits<{
  "close-notificaciones": [];
}>();

const api = useApi();
const notificacionesSinLeer = ref<NotificacionModel[]>([]);
const notificacionesLeidas = ref<NotificacionModel[]>([]);
const pestañaActiva = ref<"sinLeer" | "leidas">("sinLeer");

const numNotificacionesSinLeer = inject<Ref<number>>("numNotificacionesSinLeer")!;

const cargarNotificaciones = async () => {
  const data = await api.callStandardApi("getNotificaciones", { showLoading: false });
  if (data) {
    notificacionesSinLeer.value = data.NotificacionesSinLeer ?? [];
    notificacionesLeidas.value = data.NotificacionesLeidas ?? [];
    numNotificacionesSinLeer.value = notificacionesSinLeer.value.length;
  }
};

let intervalId: ReturnType<typeof setInterval> | undefined;

onMounted(() => {
  void cargarNotificaciones();
  intervalId = setInterval(() => void cargarNotificaciones(), 60_000);
});

watch(
  () => props.isVisible,
  (visible) => {
    if (visible) {
      void cargarNotificaciones();
    }
  },
);

const marcarTodasLeidas = async () => {
  await api.callStandardApi("readAllNotificaciones", { showLoading: false });
  await cargarNotificaciones();
};

onUnmounted(() => {
  if (intervalId !== undefined) {
    clearInterval(intervalId);
  }
});
</script>

<style scoped lang="scss">
@import "@/styles/_vars.scss";
@import "@/styles/_themevars.scss";
@import "@/styles/figma.scss";

.notifications-shell {
  position: fixed;
  inset: 0;
  z-index: 1050;
  pointer-events: none;
}

.notifications-shell__backdrop {
  position: absolute;
  inset: 0;
  background: rgba(14, 20, 26, 0.45);
  pointer-events: auto;
}
.notifications-panel {
  padding:var(--spacing-md);
  position: fixed;
  top: 72px;
  right: var(--spacing-xs);
  z-index: 1051;
  width: min(420px, calc(100vw - var(--spacing-lg)));
  max-height: calc(100vh - var(--spacing-3xl) - var(--spacing-md));
  display: flex;
  flex-direction: column;
  background: var(--background-normal-default, #fff);
  border-radius: var(--card-border-radius);
  box-shadow: 0 10px 40px rgba(14, 20, 26, 0.18);
  pointer-events: auto;
  overflow: hidden;
}

.notifications-panel__tabs {
  display: flex;
  flex-shrink: 0;
  border-bottom: var(--border-widht-sm) solid var(--border-default-default);
}

.notifications-panel__tab {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  padding: var(--spacing-sm) 0 10px;
  border: none;
  background: transparent;
  font-size: var(--font-size-body-md);
  color: var(--text-default-caption);
  position: relative;
  transition: color 0.15s ease;
}

.notifications-panel__tab-icon {
  font-size: var(--icon-size-sm);
}

.notifications-panel__tab--active {
  color: var(--text-primary-default, #d3135a);
  font-weight: 900;
}

.notifications-panel__tab--active::after {
  content: "";
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 3px;
  border-radius: 3px 3px 0 0;
  background: var(--border-default-default);
}

.notifications-panel__body {
  max-height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  -webkit-overflow-scrolling: touch;
  padding: var(--spacing-2xs) var(--spacing-2xs) var(--spacing-sm);
}

.notifications-panel__footer {
  display: flex;
  justify-content: center;
  padding: var(--spacing-3xs);
  flex-shrink: 0;
  border-top: var(--border-widht-sm) solid var(--border-default-default);
}

.notifications-panel__mark-all-read {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: var(--font-size-body-sm);
  font-weight: 600;
  color: var(--text-primary-default);
  background: transparent;
  border: none;
  margin-top: var(--spacing-2xs);

  &:hover {
    background: var(--background-primary-alt-hover);
    border-radius: 8px;
  }
}
</style>